// React e Hooks
import { useRef, useState } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Router
import { Link, useNavigate, useParams } from 'react-router-dom';

// Date Picker
import { Calendar } from 'primereact/calendar';

// MUI
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
  styled
} from '@mui/material';
import { GridDeleteIcon, GridExpandMoreIcon } from '@mui/x-data-grid';

// Emoji Picker
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';

// Editor
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Utils
import { RootState } from '../../../../../app/store';

// Config

// Store
import { atualizarCapa, atualizarCorpo, atualizarDataHoraLiberacao, atualizarResumo, atualizarSubtitulo, atualizarTitulo } from '../publicacaoSlice';

// Componentes internos

// Libs auxiliares
import { useNoticiaForm } from './useNoticiaForm';

import configSiteUrls from '../../../configs/configSiteUrls';

import CapaUpload from './CapaUpload';

import { gerarUrlCapa } from './gerarUrlCapa';
import GeralAnexosUpload from '../../licitacao/create/GeralAnexosUpload';

export function NoticiaCreateForm() {
  const publicacao = useSelector((state: RootState) => state.publicacao);
  const { id } = useParams() || '';
  const {
    imagemCapa,
    imagemUrl,
    noticiaAnexoParaSalvar,
    noticiaAnexoSalvo,
    loading,
    handleFileCapaChange,
    handleFileDeleteCapa,
    handleFileAnexoChange,
    handleOnDeleteParaSalvar,
    handleOnDeleteSalvo,
    handleExcluirTodosAnexos,
    handleGravar
  } = useNoticiaForm(id ?? '', publicacao);

  const isDateError = !publicacao.dataHoraLiberacao;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const quillRef = useRef<ReactQuill | null>(null); // Especifica que pode ser null
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const addEmoji = (emoji: { native: string }) => {
    const quill = quillRef.current?.getEditor(); // Usa o operador de encadeamento opcional
    if (quill) {
      const range = quill?.getSelection(); // Obtém a seleção atual

      if (range) {
        // Insere o emoji na posição do cursor
        quill.insertText(range.index, emoji.native + ' ', 'user'); // Adiciona um espaço após o emoji
        quill.setSelection({
          index: range.index + emoji.native.length + 1,
          length: 0
        }); // Move o cursor para a posição após o emoji
      } else {
        // Se não houver seleção, apenas adiciona ao final
        quill.setText(quill.getText() + emoji.native + ' '); // Adiciona um espaço após o emoji
      }

      // Atualiza o resumo no estado
      dispatch(atualizarCorpo(quill.root.innerHTML)); // Use o HTML para manter a formatação
    }
  };
  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1
  });

  return (
    <Paper
      elevation={3}
      sx={{
        width: {
          xs: '100%', // 100% em telas pequenas
          sm: '90%', // 90% em telas médias ou maiores
          md: '90%',
          lg: '90%'
        },
        maxWidth: '1200px', // Limite opcional
        p: 2
      }}
    >
      <Typography variant="h6" component="h1" sx={{ padding: 2 }}>
        {publicacao.id ? 'Edição' : 'Inclusão'} de Notícia
      </Typography>

      <Grid container spacing={1}>
        <Grid item xs={12} mt={4}>
          <FormControlLabel control={<Switch checked={publicacao.capa || false} onChange={e => dispatch(atualizarCapa(e.target.checked))} color="primary" />} label="Exibir no banner principal" />
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth error={isDateError}>
            <InputLabel shrink required>
              Data de Liberação
            </InputLabel>

            <Box sx={{ mt: 1 }}>
              <Calendar
                value={publicacao.dataHoraLiberacao ? new Date(publicacao.dataHoraLiberacao) : null}
                onChange={e => dispatch(atualizarDataHoraLiberacao(e.value ?? null))}
                showIcon
                dateFormat="dd/mm/yy"
                className={`p-inputtext-sm ${isDateError ? 'p-invalid' : ''}`}
                style={{
                  width: '100%',
                  padding: '10.5px 14px',
                  border: '1px solid #ced4da',
                  borderRadius: 4,
                  fontSize: '0.875rem'
                }}
              />
              {isDateError && <FormHelperText>Data de liberação é obrigatória</FormHelperText>}
            </Box>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth error={!publicacao.titulo}>
            <TextField
              required
              label="Título"
              name="titulo"
              size="small"
              inputProps={{ maxLength: 255 }}
              value={publicacao.titulo}
              onChange={e => dispatch(atualizarTitulo(e.target.value))}
              helperText={!publicacao.titulo ? 'Título é obrigatório' : `${255 - publicacao.titulo.length} caracteres restantes`}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <TextField label="Subtítulo" name="subTitulo" size="small" onChange={e => dispatch(atualizarSubtitulo(e.target.value))} inputProps={{ maxLength: 255 }} value={publicacao.subtitulo} />
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <TextField
              label="Resumo"
              name="resumo"
              size="small"
              multiline
              rows={3}
              inputProps={{ maxLength: 255 }}
              value={publicacao.resumo}
              onChange={e => dispatch(atualizarResumo(e.target.value))}
              required
            />
          </FormControl>
        </Grid>

        {/* Campo do editor Quill */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <ReactQuill
              ref={quillRef}
              value={publicacao.corpo}
              onChange={e => dispatch(atualizarCorpo(e))}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, 4, 5, 6, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  ['blockquote', 'code-block'],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  [{ script: 'sub' }, { script: 'super' }],
                  [{ indent: '-1' }, { indent: '+1' }],
                  [{ direction: 'rtl' }],
                  [{ color: [] }, { background: [] }],
                  [{ align: [] }],
                  ['link', 'image', 'video'],
                  ['clean'],
                  ['emoji'],
                  ['table'],
                  ['audio'],
                  [{ size: ['small', false, 'large', 'huge'] }]
                ]
              }}
              placeholder="Escreva a publicação aqui..."
            />
            <Divider style={{ margin: '10px 0' }} /> {/* Margem ao redor do Divider */}
            <Box display="flex" alignItems="center">
              {' '}
              {/* Alinhamento vertical dos elementos */}
              <Tooltip title="Adicionar Emoji">
                <IconButton onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                  <span role="img" aria-label="emoji">
                    😊
                  </span>
                </IconButton>
              </Tooltip>
              {showEmojiPicker && (
                <Picker
                  title="Escolha um Emoji"
                  emoji="point_up"
                  style={{ marginTop: '10px' }}
                  data={data}
                  onEmojiSelect={addEmoji} // Passa a função addEmoji como referência
                />
              )}
            </Box>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Box>
            <Accordion defaultExpanded={false}>
              <AccordionSummary expandIcon={<GridExpandMoreIcon />} aria-controls="panel2-content" id="panel3-header">
                <Typography color="secondary" variant="body1" gutterBottom>
                  Imagem de Capa - resolução sugerida 1068 x 580
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                <Paper>
                  <FormControl fullWidth>
                    <CapaUpload imagemCapa={imagemCapa} imagemUrl={imagemUrl} onFileChange={handleFileCapaChange} onDelete={handleFileDeleteCapa} />
                    {publicacao.id && publicacao.imagem && (
                      <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <img src={gerarUrlCapa(publicacao.imagem, 'NOTICIA')} alt="Miniatura" style={{ maxWidth: '100%', maxHeight: '150px' }} />

                          <Typography variant="caption" color="secondary">
                            Imagem já salva no servidor.
                          </Typography>
                        </Box>

                        <Button variant="outlined" color="error" onClick={handleFileDeleteCapa} sx={{ mt: 1 }}>
                          <GridDeleteIcon color="error" />
                        </Button>
                      </Box>
                    )}
                  </FormControl>
                </Paper>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box>
            <Accordion defaultExpanded={false}>
              <AccordionSummary expandIcon={<GridExpandMoreIcon />} aria-controls="panel2-content" id="panel4-header">
                <Typography color="secondary" variant="body1" gutterBottom>
                  Anexos
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                <Paper>
                  <GeralAnexosUpload
                    arquivosSalvos={noticiaAnexoSalvo ?? null}
                    arquivosParaSalvar={noticiaAnexoParaSalvar}
                    onFileChange={handleFileAnexoChange}
                    onDeleteSalvo={handleOnDeleteSalvo}
                    onDeleteParaSalvar={handleOnDeleteParaSalvar}
                    onDeleteAll={handleExcluirTodosAnexos}
                    tipo="NOTICIA"
                  />
                </Paper>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" gap={2}>
            <Button variant="contained" component={Link} to={configSiteUrls.SITE_NOTICIA_LIST}>
              Voltar
            </Button>
            <Button type="submit" variant="contained" color="secondary" onClick={handleGravar}>
              {loading ? <CircularProgress size={24} /> : 'Gravar'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}
